import React, { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import {
  FiSearch, FiPlus, FiEdit2, FiTrash2, FiUserCheck, FiUsers, FiSave,
  FiPhone, FiChevronLeft, FiChevronRight, FiCheckCircle,
} from 'react-icons/fi';
import { toast } from 'react-toastify';
import { staffAPI, staffAttendanceAPI } from '../../services/api';
import { formatDate } from '../../utils/helpers';
import { useAuth } from '../../context/AuthContext';
import AdminLayout from '../../components/layout/AdminLayout';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import Modal from '../../components/common/Modal';

const STATUSES = ['Present', 'Absent', 'Half Day', 'Leave'];
const ABSENT_STATUSES = ['Absent', 'Half Day', 'Leave'];

const statusStyles = {
  Present: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  Absent: 'bg-red-50 text-red-600 border-red-200',
  'Half Day': 'bg-amber-50 text-amber-700 border-amber-200',
  Leave: 'bg-sky-50 text-sky-700 border-sky-200',
};

const todayStr = () => new Date().toISOString().slice(0, 10);

const emptyStaffForm = { name: '', contact: '', department: '', remarks: '' };

const inputClass =
  'w-full px-4 py-2.5 border border-blue-100 rounded-xl text-sm focus:outline-none focus:border-blue-400 focus:ring-2 focus:ring-blue-200 text-slate-700';
const labelClass =
  'block text-slate-500 text-xs font-semibold uppercase tracking-wider mb-1.5';

const AdminStaffAttendance = () => {
  const { user } = useAuth();
  const canEdit = user?.role === 'admin' || user?.role === 'store_management';

  const [activeTab, setActiveTab] = useState(canEdit ? 'take' : 'history');

  // ── Staff registry state ─────────────────────────────────────────
  const [staffList, setStaffList] = useState([]);
  const [staffModalOpen, setStaffModalOpen] = useState(false);
  const [staffForm, setStaffForm] = useState(emptyStaffForm);
  const [editingStaff, setEditingStaff] = useState(null);
  const [savingStaff, setSavingStaff] = useState(false);

  // ── Take-attendance sheet state ──────────────────────────────────
  const [sheetDate, setSheetDate] = useState(todayStr());
  const [sheet, setSheet] = useState({}); // { staffId: { present, status, remarks } }
  const [sheetLoading, setSheetLoading] = useState(true);
  const [savingSheet, setSavingSheet] = useState(false);
  const [sheetSavedInfo, setSheetSavedInfo] = useState('');

  // ── History state ────────────────────────────────────────────────
  const [records, setRecords] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [page, setPage] = useState(1);
  const perPage = 20;

  // ── Data fetching ────────────────────────────────────────────────

  const fetchStaff = useCallback(async () => {
    try {
      const res = await staffAPI.getAll();
      setStaffList(res.data?.data?.staff || []);
    } catch {
      toast.error('Failed to load staff list.');
    }
  }, []);

  useEffect(() => { fetchStaff(); }, [fetchStaff]);

  // Build the attendance sheet: every active staff ticked Present by default,
  // overlaid with any records already saved for the chosen date.
  const loadSheet = useCallback(async () => {
    setSheetLoading(true);
    setSheetSavedInfo('');
    try {
      const res = await staffAttendanceAPI.getAll({ from: sheetDate, to: sheetDate });
      const existing = res.data?.data?.records || [];
      const byStaff = new Map(
        existing.filter((r) => r.staff).map((r) => [String(r.staff?._id || r.staff), r])
      );

      const next = {};
      staffList.filter((s) => s.isActive).forEach((s) => {
        const rec = byStaff.get(String(s._id));
        next[s._id] = rec
          ? { present: rec.status === 'Present', status: rec.status, remarks: rec.remarks || '' }
          : { present: true, status: 'Present', remarks: '' };
      });
      setSheet(next);
      if (existing.length > 0) {
        setSheetSavedInfo(`Attendance for this date was already saved (${existing.length} record(s)) — saving again will update it.`);
      }
    } catch {
      toast.error('Failed to load attendance for this date.');
    } finally {
      setSheetLoading(false);
    }
  }, [sheetDate, staffList]);

  useEffect(() => {
    if (activeTab === 'take') loadSheet();
  }, [activeTab, loadSheet]);

  const fetchHistory = useCallback(async () => {
    setHistoryLoading(true);
    try {
      const params = {};
      if (search) params.search = search;
      if (statusFilter) params.status = statusFilter;
      if (fromDate) params.from = fromDate;
      if (toDate) params.to = toDate;
      const res = await staffAttendanceAPI.getAll(params);
      setRecords(res.data?.data?.records || []);
      setPage(1);
    } catch {
      toast.error('Failed to load attendance records.');
    } finally {
      setHistoryLoading(false);
    }
  }, [search, statusFilter, fromDate, toDate]);

  useEffect(() => {
    if (activeTab !== 'history') return;
    const timer = setTimeout(fetchHistory, search ? 350 : 0);
    return () => clearTimeout(timer);
  }, [activeTab, fetchHistory, search]);

  // ── Take attendance handlers ─────────────────────────────────────

  const togglePresent = (staffId) => {
    setSheet((prev) => {
      const entry = prev[staffId];
      const present = !entry.present;
      return {
        ...prev,
        [staffId]: { ...entry, present, status: present ? 'Present' : 'Absent' },
      };
    });
  };

  const setEntry = (staffId, patch) => {
    setSheet((prev) => ({ ...prev, [staffId]: { ...prev[staffId], ...patch } }));
  };

  const markAllPresent = () => {
    setSheet((prev) => {
      const next = {};
      Object.keys(prev).forEach((id) => {
        next[id] = { ...prev[id], present: true, status: 'Present' };
      });
      return next;
    });
  };

  const handleSaveSheet = async () => {
    const entries = Object.entries(sheet).map(([staffId, entry]) => ({
      staff: staffId,
      status: entry.present ? 'Present' : entry.status,
      remarks: entry.remarks,
    }));
    if (entries.length === 0) {
      toast.info('Add staff members first in the Staff List tab.');
      return;
    }
    setSavingSheet(true);
    try {
      const res = await staffAttendanceAPI.mark({ date: sheetDate, entries });
      toast.success(res.data?.message || 'Attendance saved.');
      loadSheet();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to save attendance.');
    } finally {
      setSavingSheet(false);
    }
  };

  // ── Staff registry handlers ──────────────────────────────────────

  const openCreateStaff = () => {
    setEditingStaff(null);
    setStaffForm(emptyStaffForm);
    setStaffModalOpen(true);
  };

  const openEditStaff = (staff) => {
    setEditingStaff(staff);
    setStaffForm({
      name: staff.name || '',
      contact: staff.contact || '',
      department: staff.department || '',
      remarks: staff.remarks || '',
    });
    setStaffModalOpen(true);
  };

  const handleStaffSubmit = async (e) => {
    e.preventDefault();
    setSavingStaff(true);
    try {
      if (editingStaff) {
        await staffAPI.update(editingStaff._id, staffForm);
        toast.success('Staff member updated.');
      } else {
        await staffAPI.create(staffForm);
        toast.success('Staff member added.');
      }
      setStaffModalOpen(false);
      fetchStaff();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to save staff member.');
    } finally {
      setSavingStaff(false);
    }
  };

  const handleToggleActive = async (staff) => {
    try {
      await staffAPI.update(staff._id, { isActive: !staff.isActive });
      setStaffList((prev) =>
        prev.map((s) => (s._id === staff._id ? { ...s, isActive: !s.isActive } : s))
      );
      toast.success(staff.isActive ? 'Marked as inactive.' : 'Marked as active.');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to update staff member.');
    }
  };

  const handleDeleteStaff = async (id) => {
    if (!window.confirm('Delete this staff member? If they have attendance history, mark them inactive instead.')) return;
    try {
      await staffAPI.delete(id);
      setStaffList((prev) => prev.filter((s) => s._id !== id));
      toast.success('Staff member deleted.');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to delete staff member.');
    }
  };

  // ── History handlers ─────────────────────────────────────────────

  const handleDeleteRecord = async (id) => {
    if (!window.confirm('Delete this attendance record?')) return;
    try {
      await staffAttendanceAPI.delete(id);
      setRecords((prev) => prev.filter((r) => r._id !== id));
      toast.success('Record deleted.');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to delete record.');
    }
  };

  const activeStaff = staffList.filter((s) => s.isActive);
  const presentCount = Object.values(sheet).filter((e) => e.present).length;
  const absentCount = Object.values(sheet).length - presentCount;
  const totalPages = Math.ceil(records.length / perPage);
  const paginated = records.slice((page - 1) * perPage, page * perPage);

  const tabClass = (key) =>
    `px-4 py-2.5 rounded-xl text-sm font-semibold transition-all ${
      activeTab === key
        ? 'bg-[#1D4ED8] text-white shadow-md'
        : 'bg-white text-slate-500 border border-blue-100 hover:bg-blue-50'
    }`;

  return (
    <AdminLayout>
      <div className="mb-6 flex items-start justify-between flex-wrap gap-3">
        <div>
          <h2 className="text-2xl font-bold text-[#1e3a8a]">Non-Teaching Staff Attendance</h2>
          <p className="text-slate-500 text-sm mt-1">
            Register staff once, then take daily attendance with a single tick sheet
          </p>
        </div>
        {activeTab === 'staff' && canEdit && (
          <button onClick={openCreateStaff}
            className="flex items-center gap-2 px-4 py-2.5 text-white rounded-xl text-sm font-semibold shadow-md hover:shadow-lg transition-all"
            style={{ background: 'linear-gradient(135deg, #1e3a8a, #2563EB)' }}>
            <FiPlus className="w-4 h-4" /> Add Staff
          </button>
        )}
      </div>

      {/* Tabs */}
      <div className="flex gap-2 mb-6 flex-wrap">
        {canEdit && (
          <button onClick={() => setActiveTab('take')} className={tabClass('take')}>
            Take Attendance
          </button>
        )}
        <button onClick={() => setActiveTab('history')} className={tabClass('history')}>
          Attendance History
        </button>
        <button onClick={() => setActiveTab('staff')} className={tabClass('staff')}>
          Staff List
        </button>
      </div>

      {/* ── TAKE ATTENDANCE ─────────────────────────────────────────── */}
      {activeTab === 'take' && canEdit && (
        <>
          <div className="bg-white rounded-2xl border border-blue-100 p-4 mb-6 flex gap-3 items-center flex-wrap shadow-sm">
            <div>
              <label className="text-slate-400 text-xs font-semibold uppercase tracking-wider mr-2">Date</label>
              <input type="date" value={sheetDate} max={todayStr()}
                onChange={(e) => setSheetDate(e.target.value)}
                className="px-4 py-2.5 border border-blue-100 rounded-xl text-sm text-slate-600 focus:outline-none focus:border-blue-400" />
            </div>
            <button onClick={markAllPresent}
              className="flex items-center gap-1.5 px-3 py-2 bg-emerald-50 text-emerald-700 border border-emerald-200 rounded-xl text-xs font-semibold hover:bg-emerald-100 transition-colors">
              <FiCheckCircle className="w-3.5 h-3.5" /> Mark All Present
            </button>
            <div className="flex items-center gap-2 ml-auto text-sm">
              <span className="px-3 py-1.5 bg-emerald-50 text-emerald-700 border border-emerald-200 rounded-full text-xs font-semibold">
                {presentCount} Present
              </span>
              <span className="px-3 py-1.5 bg-red-50 text-red-600 border border-red-200 rounded-full text-xs font-semibold">
                {absentCount} Absent / Leave
              </span>
            </div>
          </div>

          {sheetSavedInfo && (
            <div className="mb-4 px-4 py-3 bg-sky-50 border border-sky-200 text-sky-700 rounded-xl text-sm">
              {sheetSavedInfo}
            </div>
          )}

          {sheetLoading ? <LoadingSpinner /> : (
            <div className="bg-white rounded-2xl border border-blue-100 shadow-sm overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-blue-50/50 border-b border-blue-100">
                      <th className="text-left px-5 py-3.5 text-slate-500 text-xs font-semibold uppercase tracking-wider w-20">Present</th>
                      <th className="text-left px-5 py-3.5 text-slate-500 text-xs font-semibold uppercase tracking-wider">Staff Name</th>
                      <th className="text-left px-5 py-3.5 text-slate-500 text-xs font-semibold uppercase tracking-wider">Department</th>
                      <th className="text-left px-5 py-3.5 text-slate-500 text-xs font-semibold uppercase tracking-wider">Status</th>
                      <th className="text-left px-5 py-3.5 text-slate-500 text-xs font-semibold uppercase tracking-wider hidden md:table-cell">Remarks</th>
                    </tr>
                  </thead>
                  <tbody>
                    {activeStaff.map((staff, idx) => {
                      const entry = sheet[staff._id];
                      if (!entry) return null;
                      return (
                        <motion.tr key={staff._id} initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: idx * 0.02 }}
                          className={`border-b border-blue-50 transition-colors ${entry.present ? 'hover:bg-blue-50/30' : 'bg-red-50/30'}`}>
                          <td className="px-5 py-3.5">
                            <input type="checkbox" checked={entry.present}
                              onChange={() => togglePresent(staff._id)}
                              className="w-5 h-5 accent-emerald-600 cursor-pointer" />
                          </td>
                          <td className="px-5 py-3.5">
                            <span className="font-semibold text-[#1e3a8a] block">{staff.name}</span>
                            {staff.contact && (
                              <span className="text-slate-400 text-xs flex items-center gap-1">
                                <FiPhone className="w-3 h-3" /> {staff.contact}
                              </span>
                            )}
                          </td>
                          <td className="px-5 py-3.5 text-slate-600">{staff.department}</td>
                          <td className="px-5 py-3.5">
                            {entry.present ? (
                              <span className={`px-2.5 py-1 rounded-full text-xs font-semibold border ${statusStyles.Present}`}>
                                Present
                              </span>
                            ) : (
                              <select value={entry.status}
                                onChange={(e) => setEntry(staff._id, { status: e.target.value })}
                                className="px-3 py-1.5 border border-red-200 bg-white rounded-xl text-xs font-semibold text-slate-600 focus:outline-none focus:border-blue-400">
                                {ABSENT_STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                              </select>
                            )}
                          </td>
                          <td className="px-5 py-3.5 hidden md:table-cell">
                            <input type="text" value={entry.remarks} placeholder="Remarks (optional)"
                              onChange={(e) => setEntry(staff._id, { remarks: e.target.value })}
                              className="w-full max-w-[240px] px-3 py-1.5 border border-blue-100 rounded-xl text-xs text-slate-600 focus:outline-none focus:border-blue-400 placeholder-blue-200" />
                          </td>
                        </motion.tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {activeStaff.length === 0 && (
                <div className="text-center py-16">
                  <FiUsers className="w-12 h-12 text-blue-200 mx-auto mb-3" />
                  <p className="text-slate-500 font-medium">No staff registered yet</p>
                  <p className="text-slate-400 text-sm mt-1">
                    Go to the "Staff List" tab and add staff members first — then take attendance here.
                  </p>
                </div>
              )}

              {activeStaff.length > 0 && (
                <div className="flex justify-end p-4 border-t border-blue-50">
                  <button onClick={handleSaveSheet} disabled={savingSheet}
                    className="flex items-center gap-2 px-6 py-3 text-white rounded-xl text-sm font-bold shadow-md hover:shadow-lg transition-all disabled:opacity-60"
                    style={{ background: 'linear-gradient(135deg, #1e3a8a, #2563EB)' }}>
                    <FiSave className="w-4 h-4" />
                    {savingSheet ? 'Saving...' : `Save Attendance (${formatDate(sheetDate)})`}
                  </button>
                </div>
              )}
            </div>
          )}
        </>
      )}

      {/* ── HISTORY ─────────────────────────────────────────────────── */}
      {activeTab === 'history' && (
        <>
          <div className="bg-white rounded-2xl border border-blue-100 p-4 mb-6 flex gap-3 items-center flex-wrap shadow-sm">
            <div className="relative flex-1 min-w-[200px]">
              <FiSearch className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400 w-4 h-4" />
              <input type="text" placeholder="Search by staff name..."
                value={search} onChange={(e) => setSearch(e.target.value)}
                className="w-full pl-10 pr-4 py-2.5 border border-blue-100 rounded-xl text-sm focus:outline-none focus:border-blue-400 focus:ring-2 focus:ring-blue-200 text-slate-700 placeholder-blue-200"
              />
            </div>
            <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}
              className="px-4 py-2.5 border border-blue-100 rounded-xl text-sm text-slate-600 focus:outline-none focus:border-blue-400">
              <option value="">All statuses</option>
              {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
            <input type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)}
              className="px-4 py-2.5 border border-blue-100 rounded-xl text-sm text-slate-600 focus:outline-none focus:border-blue-400"
              title="From date" />
            <input type="date" value={toDate} onChange={(e) => setToDate(e.target.value)}
              className="px-4 py-2.5 border border-blue-100 rounded-xl text-sm text-slate-600 focus:outline-none focus:border-blue-400"
              title="To date" />
            <div className="text-sm text-slate-400 flex-shrink-0">
              {records.length} record{records.length !== 1 ? 's' : ''}
            </div>
          </div>

          {historyLoading ? <LoadingSpinner /> : (
            <div className="bg-white rounded-2xl border border-blue-100 shadow-sm overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-blue-50/50 border-b border-blue-100">
                      <th className="text-left px-5 py-3.5 text-slate-500 text-xs font-semibold uppercase tracking-wider">Staff Name</th>
                      <th className="text-left px-5 py-3.5 text-slate-500 text-xs font-semibold uppercase tracking-wider">Department</th>
                      <th className="text-left px-5 py-3.5 text-slate-500 text-xs font-semibold uppercase tracking-wider">Date</th>
                      <th className="text-left px-5 py-3.5 text-slate-500 text-xs font-semibold uppercase tracking-wider">Status</th>
                      <th className="text-left px-5 py-3.5 text-slate-500 text-xs font-semibold uppercase tracking-wider hidden lg:table-cell">Remarks</th>
                      {canEdit && (
                        <th className="text-left px-5 py-3.5 text-slate-500 text-xs font-semibold uppercase tracking-wider">Actions</th>
                      )}
                    </tr>
                  </thead>
                  <tbody>
                    {paginated.map((record, idx) => (
                      <motion.tr key={record._id} initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: idx * 0.03 }}
                        className="border-b border-blue-50 hover:bg-blue-50/30 transition-colors">
                        <td className="px-5 py-4 font-semibold text-[#1e3a8a]">{record.staffName}</td>
                        <td className="px-5 py-4 text-slate-600">{record.department}</td>
                        <td className="px-5 py-4 text-slate-400 text-xs whitespace-nowrap">{formatDate(record.date)}</td>
                        <td className="px-5 py-4">
                          <span className={`px-2.5 py-1 rounded-full text-xs font-semibold border ${statusStyles[record.status] || 'bg-blue-50 text-blue-700 border-blue-100'}`}>
                            {record.status}
                          </span>
                        </td>
                        <td className="px-5 py-4 hidden lg:table-cell text-slate-500 max-w-[220px]">
                          <p className="truncate">{record.remarks || '—'}</p>
                        </td>
                        {canEdit && (
                          <td className="px-5 py-4">
                            <button onClick={() => handleDeleteRecord(record._id)}
                              className="p-1.5 text-red-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors" title="Delete">
                              <FiTrash2 className="w-4 h-4" />
                            </button>
                          </td>
                        )}
                      </motion.tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {paginated.length === 0 && (
                <div className="text-center py-16">
                  <FiUserCheck className="w-12 h-12 text-blue-200 mx-auto mb-3" />
                  <p className="text-slate-500 font-medium">No attendance records found</p>
                  {canEdit && (
                    <p className="text-slate-400 text-sm mt-1">Use the "Take Attendance" tab to record a day.</p>
                  )}
                </div>
              )}

              {totalPages > 1 && (
                <div className="flex items-center justify-center gap-2 p-4 border-t border-blue-50">
                  <button onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page === 1}
                    className="p-2 rounded-xl border border-blue-100 hover:bg-blue-50 disabled:opacity-40 transition-colors">
                    <FiChevronLeft className="w-4 h-4 text-slate-500" />
                  </button>
                  <span className="text-sm text-slate-500 px-3">Page {page} of {totalPages}</span>
                  <button onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={page === totalPages}
                    className="p-2 rounded-xl border border-blue-100 hover:bg-blue-50 disabled:opacity-40 transition-colors">
                    <FiChevronRight className="w-4 h-4 text-slate-500" />
                  </button>
                </div>
              )}
            </div>
          )}
        </>
      )}

      {/* ── STAFF LIST ──────────────────────────────────────────────── */}
      {activeTab === 'staff' && (
        <div className="bg-white rounded-2xl border border-blue-100 shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-blue-50/50 border-b border-blue-100">
                  <th className="text-left px-5 py-3.5 text-slate-500 text-xs font-semibold uppercase tracking-wider">Name</th>
                  <th className="text-left px-5 py-3.5 text-slate-500 text-xs font-semibold uppercase tracking-wider">Contact</th>
                  <th className="text-left px-5 py-3.5 text-slate-500 text-xs font-semibold uppercase tracking-wider">Department</th>
                  <th className="text-left px-5 py-3.5 text-slate-500 text-xs font-semibold uppercase tracking-wider">Status</th>
                  <th className="text-left px-5 py-3.5 text-slate-500 text-xs font-semibold uppercase tracking-wider hidden lg:table-cell">Remarks</th>
                  {canEdit && (
                    <th className="text-left px-5 py-3.5 text-slate-500 text-xs font-semibold uppercase tracking-wider">Actions</th>
                  )}
                </tr>
              </thead>
              <tbody>
                {staffList.map((staff, idx) => (
                  <motion.tr key={staff._id} initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: idx * 0.03 }}
                    className={`border-b border-blue-50 hover:bg-blue-50/30 transition-colors ${!staff.isActive ? 'opacity-60' : ''}`}>
                    <td className="px-5 py-4 font-semibold text-[#1e3a8a]">{staff.name}</td>
                    <td className="px-5 py-4 text-slate-600">
                      {staff.contact ? (
                        <span className="flex items-center gap-1.5">
                          <FiPhone className="w-3.5 h-3.5 text-slate-400" /> {staff.contact}
                        </span>
                      ) : '—'}
                    </td>
                    <td className="px-5 py-4 text-slate-600">{staff.department}</td>
                    <td className="px-5 py-4">
                      <button onClick={() => canEdit && handleToggleActive(staff)} disabled={!canEdit}
                        className={`px-2.5 py-1 rounded-full text-xs font-semibold border transition-colors ${
                          staff.isActive
                            ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
                            : 'bg-slate-100 text-slate-500 border-slate-200'
                        } ${canEdit ? 'cursor-pointer hover:opacity-80' : 'cursor-default'}`}
                        title={canEdit ? 'Click to toggle active status' : undefined}>
                        {staff.isActive ? 'Active' : 'Inactive'}
                      </button>
                    </td>
                    <td className="px-5 py-4 hidden lg:table-cell text-slate-500 max-w-[200px]">
                      <p className="truncate">{staff.remarks || '—'}</p>
                    </td>
                    {canEdit && (
                      <td className="px-5 py-4">
                        <div className="flex items-center gap-2">
                          <button onClick={() => openEditStaff(staff)}
                            className="p-1.5 text-blue-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors" title="Edit">
                            <FiEdit2 className="w-4 h-4" />
                          </button>
                          <button onClick={() => handleDeleteStaff(staff._id)}
                            className="p-1.5 text-red-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors" title="Delete">
                            <FiTrash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    )}
                  </motion.tr>
                ))}
              </tbody>
            </table>
          </div>

          {staffList.length === 0 && (
            <div className="text-center py-16">
              <FiUsers className="w-12 h-12 text-blue-200 mx-auto mb-3" />
              <p className="text-slate-500 font-medium">No staff registered yet</p>
              {canEdit && (
                <p className="text-slate-400 text-sm mt-1">
                  Click "Add Staff" to register kitchen staff, bus conductors, helpers, guards...
                </p>
              )}
            </div>
          )}
        </div>
      )}

      {/* Add/Edit Staff Modal */}
      <Modal isOpen={staffModalOpen} onClose={() => setStaffModalOpen(false)}
        title={editingStaff ? 'Edit Staff Member' : 'Add Staff Member'} size="md">
        <form onSubmit={handleStaffSubmit} className="space-y-4">
          <div>
            <label className={labelClass}>Name *</label>
            <input className={inputClass} required value={staffForm.name}
              onChange={(e) => setStaffForm({ ...staffForm, name: e.target.value })} />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className={labelClass}>Contact Number</label>
              <input className={inputClass} value={staffForm.contact}
                onChange={(e) => setStaffForm({ ...staffForm, contact: e.target.value })}
                placeholder="98XXXXXXXX" />
            </div>
            <div>
              <label className={labelClass}>Department / Branch *</label>
              <input className={inputClass} required value={staffForm.department}
                onChange={(e) => setStaffForm({ ...staffForm, department: e.target.value })}
                placeholder="e.g. Kitchen, Bus Conductor, Security" list="department-suggestions" />
              <datalist id="department-suggestions">
                {[...new Set(staffList.map((s) => s.department).filter(Boolean))].map((d) => (
                  <option key={d} value={d} />
                ))}
              </datalist>
            </div>
          </div>
          <div>
            <label className={labelClass}>Remarks</label>
            <textarea className={inputClass} rows={2} value={staffForm.remarks}
              onChange={(e) => setStaffForm({ ...staffForm, remarks: e.target.value })} />
          </div>
          <div className="flex justify-end gap-3 pt-2">
            <button type="button" onClick={() => setStaffModalOpen(false)}
              className="px-5 py-2.5 border border-blue-100 rounded-xl text-sm font-semibold text-slate-500 hover:bg-blue-50 transition-colors">
              Cancel
            </button>
            <button type="submit" disabled={savingStaff}
              className="px-5 py-2.5 text-white rounded-xl text-sm font-semibold shadow-md disabled:opacity-60"
              style={{ background: 'linear-gradient(135deg, #1e3a8a, #2563EB)' }}>
              {savingStaff ? 'Saving...' : editingStaff ? 'Update Staff' : 'Add Staff'}
            </button>
          </div>
        </form>
      </Modal>
    </AdminLayout>
  );
};

export default AdminStaffAttendance;
